/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
/**
 * ProposalViewHostContext — app-level signal that the current page's primary
 * content IS a drafted proposal (today: the AI action detail page,
 * `/actions/:id`). Any CRM record surface open while such a page is mounted is
 * a side view of that proposal, not a standalone slideout.
 *
 * Read by {@link EmailThreadReplyFooter} to hide the reply footer — replying
 * is already the proposal's job. The provider sits above BOTH the router
 * `Outlet` and the global `SlideoutRenderer`, so the signal reaches the record
 * surface whether the page hosts it inline (its SplitView entity panel) or the
 * global overlay renders it (narrow / mobile viewports, where the inline host
 * claim is dropped). A page-local React context could only reach the inline
 * case; the fallback overlay renders outside the page's subtree.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface ProposalViewHostContextValue {
  /** True while at least one mounted host has claimed proposal-view status. */
  isHostedOnProposalView: boolean;
  /**
   * Claim proposal-view host status for the caller's lifetime. Returns an
   * unregister function; call it (or let an effect cleanup call it) on
   * unmount. Ref-counted so overlapping claims during a route transition keep
   * the flag true until the last host releases.
   */
  registerProposalViewHost: () => () => void;
}

const ProposalViewHostContext = createContext<ProposalViewHostContextValue>({
  isHostedOnProposalView: false,
  registerProposalViewHost: () => () => {},
});

export function ProposalViewHostProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [claimCount, setClaimCount] = useState(0);

  const registerProposalViewHost = useCallback(() => {
    setClaimCount(count => count + 1);
    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      setClaimCount(count => Math.max(0, count - 1));
    };
  }, []);

  const value = useMemo<ProposalViewHostContextValue>(
    () => ({
      isHostedOnProposalView: claimCount > 0,
      registerProposalViewHost,
    }),
    [claimCount, registerProposalViewHost]
  );

  return (
    <ProposalViewHostContext value={value}>{children}</ProposalViewHostContext>
  );
}

/** Access the full context (registration + flag). */
export function useProposalViewHost(): ProposalViewHostContextValue {
  return useContext(ProposalViewHostContext);
}

/** Convenience read for consumers that only need the flag. */
export function useIsHostedOnProposalView(): boolean {
  return useContext(ProposalViewHostContext).isHostedOnProposalView;
}
